// copy of packages/integrations/harbor/shared/src/vault.ts, kept close to
// it so the copies merge into one shared helper (INT-576). Change both or
// neither.
import {
  UNSUPPORTED_AWS_REGION,
  findAwsRegionProblem,
  isAwsAccountId,
} from "./aws-hosting.ts";

/** The cloud each kind of vault is in, as a connector's hosting names it. A
 * connector reads a secret with its own cloud's identity, so an instance's
 * vault has to be in its connector's cloud. */
export const VAULT_CLOUDS = { "aws-sm": "aws", "gcp-sm": "gcp" } as const;

// A Google Cloud project's ID, as Google takes one. It becomes a segment of
// the path the connector reads the secret at.
const GCP_PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

export const MISSING_VAULT =
  "Choose the secret manager that holds the p0-connector account's API token.";

export const INVALID_AWS_VAULT_ACCOUNT =
  "Choose the AWS Secrets Manager installation, by its 12-digit AWS account ID, that holds the p0-connector account's API token.";

export const INVALID_AWS_VAULT_REGION =
  "Enter the AWS region the secret is in, such as us-west-2.";

export const INVALID_GCP_VAULT_PROJECT =
  "Choose the Google Secret Manager installation, by its Google Cloud project ID, that holds the p0-connector account's API token.";

/** A vault a connector can be pointed at a secret in, each value as P0
 * stores it. */
export type Vault =
  | { type: "aws-sm"; install: string; secretsRegion: string }
  | { type: "gcp-sm"; install: string };

/** A field of a vault that can't be right, by its path in the instance, and
 * what to enter instead. */
export type VaultProblem = { field: string; message: string };

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Reads `vault`, a value from a request, as a vault a connector can be
 * pointed at a secret in: the vault with each value trimmed, as P0 stores
 * it, or the first problem that keeps it from being one.
 *
 * A vault has to name its type: the install framework would take a request
 * without one for AWS Secrets Manager, whatever cloud the connector runs in.
 * No message repeats a value, which may be credentials pasted in its place.
 */
export const readVault = (
  vault: unknown
): { problem: VaultProblem } | { vault: Vault } => {
  const { install, secretsRegion, type } = isRecord(vault) ? vault : {};
  if (type !== "aws-sm" && type !== "gcp-sm") {
    return { problem: { field: "vault", message: MISSING_VAULT } };
  }
  if (type === "gcp-sm") {
    return typeof install === "string" &&
      GCP_PROJECT_ID_PATTERN.test(install.trim())
      ? { vault: { type, install: install.trim() } }
      : {
          problem: {
            field: "vault/gcp-sm/install",
            message: INVALID_GCP_VAULT_PROJECT,
          },
        };
  }
  if (!isAwsAccountId(install)) {
    return {
      problem: {
        field: "vault/aws-sm/install",
        message: INVALID_AWS_VAULT_ACCOUNT,
      },
    };
  }
  const regionProblem = findAwsRegionProblem(secretsRegion);
  if (regionProblem || typeof secretsRegion !== "string") {
    return {
      problem: {
        field: "vault/aws-sm/secretsRegion",
        // The region rule's own words are about the connector.
        message:
          regionProblem === UNSUPPORTED_AWS_REGION
            ? UNSUPPORTED_AWS_REGION
            : INVALID_AWS_VAULT_REGION,
      },
    };
  }
  return {
    vault: {
      type,
      install: install.trim(),
      secretsRegion: secretsRegion.trim(),
    },
  };
};
